import { Injectable } from '@angular/core';

import { OfferSource } from '@cds';
import { Fixture } from '@cds/betting-offer';
import { EventModel, EventOption, EventOptionGroup, EventParticipant } from '@frontend/sports/betting-offer/feature/model';
import { BetslipUpsellConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { BetslipDisplayMode } from '@frontend/sports/types/components/bet-placement';
import { BetslipUpsellMarketsGroup } from '@frontend/sports/types/components/betslip-upsell';
import { Store } from '@ngrx/store';
import { BetslipUpsellConstants } from 'packages/sports/web/app/src/betslip-base/models/betslip-upsell.model';
import { Observable, combineLatest, filter, map, pairwise, startWith, switchMap, tap } from 'rxjs';

import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState, IBetslipState } from '../../../base/store/state';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipUnknownPick } from '../../../core/picks/betslip-unknown-pick';
import { V1PickId, V2OptionMarketPickId } from '../../../core/picks/pick-id';
import BetslipPickFactory from '../../picks/factory/betslip-pick.factory';
import BetslipPickUtilsService from '../../picks/services/betslip-pick-utils.service';
import { TranslationMapping, UpsellEvent } from '../models';
import UpsellTrackingActions from '../state/upsell-tracking.actions';
import { UpsellTracking } from '../state/upsell-tracking.effects';
import { UpsellBetTypeService } from './upsell-bet-type.service';
import { UpsellDataService } from './upsell-data.service';

const upsellTrackingSource = BetslipUpsellConstants.TrackingSource.toLowerCase();

@Injectable()
export class UpsellEventFactoryService {
    private initialEmit = true;

    constructor(
        private sitecore: Sitecore,
        private betslipUpsellConfig: BetslipUpsellConfig,
        private pickFactory: BetslipPickFactory,
        private betslipPickUtils: BetslipPickUtilsService,
        private upsellService: UpsellDataService,
        private store: Store<IBetslipRootState>,
        private upsellBetTypeService: UpsellBetTypeService,
    ) {}

    getUpsellEvents$(): Observable<UpsellEvent[]> {
        //TODO below selector "betslipSelector" is too generic, and will emit a lot, please use more targeted selector for both selectedPicks$ and upsellData$
        //Update @Monzer ignored during v3 cleanup (still valid)
        const selectedPicks$ = this.store
            .select(betslipSelector)
            .pipe(map((betslipState) => this.betslipPickUtils.getSelectedPicks(betslipState.types, betslipState.picks.pickList)));
        const upsellData$ = this.store.select(betslipSelector).pipe(
            filter((betslipState) => !betslipState?.picks.pickList.some((pick) => BetslipUnknownPick.isPick(pick))),
            startWith(undefined),
            pairwise(),
            filter(([previousBetslipState, currentBetslipState]) => {
                //we need to show new upsell container only when non-upsell pick is added
                const previousNonUpsellPickCount = this.getSelectedPicks(previousBetslipState).filter(
                    (pick) => !pick.tracking?.source?.toLowerCase().includes(upsellTrackingSource),
                ).length;

                const currentNonUpsellPickCount = this.getSelectedPicks(currentBetslipState).filter(
                    (pick) => !pick.tracking?.source?.toLowerCase().includes(upsellTrackingSource),
                ).length;
                const isPicksCountChange = previousNonUpsellPickCount !== currentNonUpsellPickCount;

                const previousBetType = previousBetslipState?.types.base.currentSelectedType;
                const currentBetType = currentBetslipState?.types.base.currentSelectedType;
                const isBetTypeChange = previousBetType !== currentBetType && currentBetslipState?.base.displayMode === BetslipDisplayMode.Tabbed;
                const isValidForCurrentState =
                    !!currentBetslipState &&
                    (this.upsellBetTypeService.showUpsellForSingle(currentNonUpsellPickCount, currentBetslipState) ||
                        this.upsellBetTypeService.showUpsellForMulti(currentNonUpsellPickCount, currentBetslipState));

                return (isPicksCountChange || isBetTypeChange) && isValidForCurrentState;
            }),
            map(([_, currentBetslipState]) => currentBetslipState!),
            map((betslipState) => this.upsellService.getPickDetails(betslipState)),
            filter((picks) => picks.length > 0),
            switchMap((upsellPicks) => this.upsellService.getUpsellEventData$(upsellPicks)),
        );

        return combineLatest([selectedPicks$, upsellData$]).pipe(
            map(([selectedPicks, data]) =>
                data.events
                    .map(({ event, upsellData }) =>
                        this.createUpsellEvent(
                            event,
                            upsellData.fixture,
                            upsellData.bestSellingOptionId,
                            upsellData.bestSellingMarketId.toString(),
                            selectedPicks,
                            data.gridGroupId,
                        ),
                    )
                    .filter(hasValue),
            ),
            tap((t) => {
                if (t.length > 0 && this.initialEmit) {
                    this.store.dispatch(
                        UpsellTrackingActions.track({
                            eventDetails: UpsellTracking.UpsellModule,
                            action: UpsellTracking.Load,
                        }),
                    );
                    this.initialEmit = false;
                }
            }),
        );
    }

    private createUpsellEvent(
        event: EventModel,
        fixture: Fixture,
        bestSellingOptionId: number,
        bestSellingOptionGroupId: string,
        selectedPicks: BetslipPick[],
        gridGroupId?: string,
    ): UpsellEvent | undefined {
        const bestSellingOptionGroup = event.optionGroups.find((group) => group.id === bestSellingOptionGroupId);
        const bestSellingOption = bestSellingOptionGroup?.options.find((option) => option.id === bestSellingOptionId);

        if (bestSellingOptionGroup?.options && bestSellingOption && gridGroupId) {
            const upsellMarketGroup = this.betslipUpsellConfig.marketGridGroupIds.find((group) => group.gridGroupId === gridGroupId);
            const { winningMarket, showParticipantImage } = this.getWinningMarketDetails(
                bestSellingOptionGroup,
                bestSellingOption,
                upsellMarketGroup,
            );
            const eventParticipant = this.getUpsellParticipant(event, bestSellingOptionGroup, bestSellingOption, upsellMarketGroup);
            const bestsellingPickId =
                fixture.source === OfferSource.V1
                    ? new V1PickId(fixture.id, bestSellingOption.optionGroupId, bestSellingOption.id)
                    : new V2OptionMarketPickId(fixture.id, bestSellingOption.optionGroupId, bestSellingOption.id, bestSellingOption.priceId!);

            const tracking = { source: BetslipUpsellConstants.TrackingSource };
            const bestSellingPick = this.pickFactory.createPick(bestsellingPickId, tracking, fixture);
            if (!bestSellingPick) {
                return;
            }

            const isSelected = selectedPicks.some((pick) => pick.id.isEqual(bestsellingPickId));

            return {
                event,
                bestSellingOption,
                bestSellingOptionGroup,
                bestSellingPick,
                winningMarket,
                showParticipantImage,
                eventParticipant,
                isSelected,
            };
        }

        return;
    }

    private getUpsellParticipant(
        event: EventModel,
        optionGroup: EventOptionGroup,
        trendingOption: EventOption,
        upsellMarketGroup: BetslipUpsellMarketsGroup | undefined,
    ): EventParticipant | undefined {
        if (!optionGroup.options.length || !event.participants.length || !upsellMarketGroup?.showParticipantLogo) {
            return;
        }
        if (optionGroup.options.length === 2) {
            const participantIndex = optionGroup.options.findIndex((option) => option.id === trendingOption.id);

            return participantIndex >= 0 && participantIndex < event.participants.length ? event.participants[participantIndex] : undefined;
        } else if (optionGroup.options.length === 3) {
            let participantIndex = optionGroup.options.findIndex((option) => option.id === trendingOption.id);
            if (participantIndex < 0 || participantIndex === optionGroup.options.length - 2) {
                return;
            }
            if (participantIndex === optionGroup.options.length - 1) {
                participantIndex = event.participants.length - 1;
            }

            return event.participants[participantIndex];
        }

        return;
    }

    private getWinningMarketDetails(
        optionGroup: EventOptionGroup,
        trendingOption: EventOption,
        upsellMarketGroup: BetslipUpsellMarketsGroup | undefined,
    ): { winningMarket: string; showParticipantImage: boolean } {
        const showParticipantImage = upsellMarketGroup?.showParticipantLogo ?? false;
        if (!upsellMarketGroup?.translationMapping) {
            return { winningMarket: '', showParticipantImage };
        }
        if (upsellMarketGroup.translationMapping.toLowerCase() === TranslationMapping.ToWin && optionGroup.options.length === 3) {
            const participantIndex = optionGroup.options.findIndex((option) => option.id === trendingOption.id);
            if (participantIndex > 0 && participantIndex < optionGroup.options.length - 1) {
                return {
                    winningMarket: this.sitecore.betslipUpsell.messages.DrawMessage || '',
                    showParticipantImage: false,
                };
            }
        }

        const winningMarket = interpolateDoubleBracesString(this.sitecore.betslipUpsell.marketMessages[upsellMarketGroup.translationMapping], {
            value: trendingOption.name,
        });

        return {
            winningMarket,
            showParticipantImage,
        };
    }

    private getSelectedPicks(betslipState: IBetslipState | undefined): BetslipPick[] {
        return betslipState ? this.betslipPickUtils.getSelectedPicks(betslipState.types, betslipState.picks.pickList) : [];
    }
}
